import { CLIMBING_STYLES } from '../constants.js';
import { isCleanAscent } from '../ascentTypes.js';  // Import the new function

import {
  calculateCGP,
  calculateCGE,
  calculateFirstGoGrade,
  calculateSuccessRates
} from '../metrics/index.js';

export const calculateKeyMetrics = (data, climbingStyle = CLIMBING_STYLES.SPORT, yearMode = 'all') => {
  if (!data?.length) return null;
  
  const cleanAscents = data.filter(d => d.isClean);
  const failedAscents = data.filter(d => !d.isClean);
  const grades = cleanAscents
    .map(d => climbingStyle === CLIMBING_STYLES.BOULDER ? d.vgrade : d.ewbanks)
    .filter(g => g !== null && !isNaN(g));
  
  const successRates = calculateSuccessRates(data, climbingStyle);
  const firstGoEstimate = calculateFirstGoGrade(data, climbingStyle, yearMode);
  const cgp = calculateCGP(data, climbingStyle);
  const cge = calculateCGE(data, climbingStyle);
  
  // Get unique clean routes count
  const cleanRouteIds = new Set(
    cleanAscents.map(ascent => ascent["Route ID"])
  );
  
  // Get unique dates count
  const uniqueDates = new Set(
    data.map(ascent => ascent["Ascent Date"].split('T')[0])
  );
  const totalDays = uniqueDates.size;

  // Calculate CGP per day
  const cgpPerDay = cgp && totalDays ? 
    (climbingStyle === CLIMBING_STYLES.BOULDER ?
      Math.log(Math.pow(Math.E, cgp) / totalDays) :
      Math.log2(Math.pow(2, cgp) / totalDays)) :
    0;

  // Calculate CGE per day
  const cgePerDay = cge && totalDays ? 
    (climbingStyle === CLIMBING_STYLES.BOULDER ?
      Math.log(Math.pow(Math.E, cge) / totalDays) :
      Math.log2(Math.pow(2, cge) / totalDays)) :
    0;

  // Calculate average grade but don't format it - let the UI handle formatting
  const averageCleanGrade = grades.length ? 
    grades.reduce((a, b) => a + b, 0) / grades.length :
    null;
    
  const dogListRoutes = data.reduce((acc, ascent) => {
    const routeId = ascent['Route ID'];
    const existingRoute = acc.find(r => r['Route ID'] === routeId);
    
    if (!existingRoute) {
      const allAttemptsForRoute = data.filter(a => a['Route ID'] === routeId);
      
      // Use new isCleanAscent function to check for clean ascents
      const hasCleanAscent = allAttemptsForRoute.some(a => 
        isCleanAscent(a['Ascent Type'], a['Route Gear Style'])
      );
      
      if (!hasCleanAscent) {
        acc.push(ascent);
      }
    }
    
    return acc;
  }, []);

  // Rest of the return statement remains the same
  return {
    totalAscents: data.length,
    cleanAscents: cleanAscents.length,
    failedAscents: failedAscents.length,
    distinctRoutes: successRates.totalRoutes,
    cleanRoutes: cleanRouteIds.size,
    highestCleanGrade: grades.length ? 
      (climbingStyle === CLIMBING_STYLES.BOULDER ? Math.max(...grades) : Math.max(...grades)) : 
      null,
    averageCleanGrade,
    firstGoGrade: firstGoEstimate?.currentGrade,
    flashSlope: firstGoEstimate?.slope,
    gradeByYear: firstGoEstimate?.gradeByYear,
    onsightRate: successRates.onsightRate,
    flashRate: successRates.flashRate,
    firstTryRate: successRates.firstTryRate,
    sendRate: climbingStyle === CLIMBING_STYLES.BOULDER ? 
      ((successRates.sendCount / successRates.totalRoutes) * 100) : undefined,
    repeatRate: climbingStyle === CLIMBING_STYLES.BOULDER ? 
      ((successRates.repeatCount / successRates.totalRoutes) * 100) : undefined,
    cgp,
    cgpPerDay,
    cge,
    cgePerDay,
    dogListRoutes
  };
};